import { BadgeCheck, MapPin, Languages, CalendarCheck, Award, ArrowUpRight } from "lucide-react";
import { Reveal, Chapter, TrackCorner } from "./primitives";
import { scrollToId } from "../../lib/smooth";
import { useLanguage } from "../../i18n/LanguageContext";

export const ProfileCard = () => {
  const { t } = useLanguage();
  const P = t.profile;
  return (
    <section className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="profile-section">
      <div className="pointer-events-none absolute -left-28 bottom-0 text-kn-navy/[0.05]">
        <TrackCorner className="h-[320px] w-[320px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <Chapter index="" label={P.eyebrow} />
            <h2 className="mt-6 text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">{P.h2}</h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-kn-navy/60">{P.sub}</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-2xl border border-kn-navy/10 bg-white shadow-[0_40px_100px_-40px_rgba(4,32,63,0.35)]" data-testid="profile-card">
              {/* header */}
              <div className="bg-tile-gradient relative p-8">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-5">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-kn-cyan/40 bg-kn-navy font-serif text-xl italic text-kn-cyan">
                      AK
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-white">{P.name}</p>
                      <p className="mt-0.5 text-sm text-white/60">{P.role}</p>
                      <p className="mt-1 flex items-center gap-1.5 font-mono text-[11px] text-white/50">
                        <MapPin className="h-3 w-3" strokeWidth={1.5} />
                        {P.location}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-kn-cyan/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-kn-cyan">
                    <BadgeCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Krida Verified
                  </span>
                </div>
              </div>

              {/* stats */}
              <div className="grid grid-cols-3 divide-x divide-kn-navy/10 border-b border-kn-navy/10">
                {P.stats.map((s) => (
                  <div key={s.label} className="p-5 text-center">
                    <p className="text-2xl font-semibold text-kn-navy">{s.value}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-wider text-kn-navy/45">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* meta */}
              <div className="divide-y divide-kn-navy/[0.07]">
                {P.meta.map((m, i) => {
                  const icons = [Award, Languages, CalendarCheck];
                  const Icon = icons[i];
                  return (
                    <div key={m.label} className="flex items-center justify-between px-8 py-4">
                      <span className="flex items-center gap-3 text-sm text-kn-navy/55">
                        <Icon className="h-4 w-4 text-kn-blue" strokeWidth={1.25} />
                        {m.label}
                      </span>
                      <span className="text-sm font-medium text-kn-navy">{m.value}</span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-8 py-4">
                  <span className="text-sm text-kn-navy/55">Recent assignment</span>
                  <span className="text-sm font-medium text-kn-navy">{P.recent}</span>
                </div>
              </div>

              {/* actions */}
              <div className="flex gap-3 border-t border-kn-navy/10 p-6">
                {P.actions.map((a, i) => (
                  <button
                    key={a}
                    data-testid={`profile-action-${i}`}
                    onClick={() => scrollToId("#join")}
                    className={`group flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-[13px] font-semibold transition-transform duration-300 hover:-translate-y-0.5 ${
                      i === 0 ? "bg-brand-gradient text-white" : "border border-kn-navy/15 text-kn-navy"
                    }`}
                  >
                    {a}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-4 text-right font-mono text-[10px] uppercase tracking-[0.24em] text-kn-navy/35" data-testid="profile-illustrative">
              {P.illustrative}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
